﻿import React, {useMemo} from 'react';
import {useBuyersBySupplier} from "../../hooks/components/location/useBuyersBySupplier";
import {AutocompleteOption} from "../SortFilter/interfaces";
import {Autocomplete, AutocompleteProps, AutocompleteRenderInputParams, TextField} from "@mui/material";
import {useAutocompleteOptions} from "../../hooks/autocomplete/useAutocompleteOptions";

interface BuyerNameAutocompleteProps extends Omit<
  AutocompleteProps<AutocompleteOption, false, true, false>,
  'renderInput' | 'options' | 'value'
> {
  /** Accepts a string ID or a full AutocompleteOption; resolved internally before passing to MUI. */
  value?: string | AutocompleteOption | null;
}

/**
 * Autocomplete component for selecting a buyer by company name with options fetched based on the supplier context.
 * @param props.value - The current value, which can be a string ID or an AutocompleteOption.
 * The component will resolve it to the correct option object.
 * @param props - Additional props are passed through to the underlying MUI Autocomplete component.
 */
const BuyerNameAutocomplete = (props: BuyerNameAutocompleteProps) => {
  const {value, ...rest} = props;
  const buyers = useBuyersBySupplier();

  const buyerOptions = useMemo<Array<AutocompleteOption>>(() =>
    buyers.map(buyer => ({ id: buyer.value, label: buyer.label })),
    [buyers]
  );

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <TextField {...params} label={'Buyer'} variant={'standard'} />
  );

  const {
    handleGetOptionLabel,
    handleIsOptionEqualToValue,
    filterOptions,
    resolvedValue
  } = useAutocompleteOptions({ autocompleteOptions: buyerOptions, value, filterMode: 'flexible' });

 /**
  * Falls back to buyerOptions[0] ('All' option) when value is undefined/null,
  * since disableClearable requires a non-null value and buyers is guaranteed
  * to always have at least the 'All' option.
  */
  return buyerOptions.length > 1 ? (
    <Autocomplete
      disablePortal
      disableClearable
      renderInput={renderInput}
      options={buyerOptions}
      filterOptions={filterOptions}
      getOptionLabel={handleGetOptionLabel}
      getOptionKey={(option) => option.id}
      isOptionEqualToValue={handleIsOptionEqualToValue}
      value={resolvedValue ?? buyerOptions[0]}
      {...rest}
    />
  ) : null;
}

export { BuyerNameAutocomplete };
